import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import VerifiedIcon from "@mui/icons-material/VerifiedUser";
import VideocamIcon from "@mui/icons-material/Videocam";
import LockIcon from "@mui/icons-material/Lock";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SearchIcon from "@mui/icons-material/Search";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import { useAuth } from "@/auth/AuthContext";

export default function HomePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, register, registerAsDoctor } = useAuth();

  return (
    <Box>
      {/* ───────── Hero ───────── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.primary.light,
            0.18,
          )} 0%, transparent 70%)`,
        }}
      >
        <DecorBlobs />
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                icon={<VerifiedIcon />}
                color="primary"
                variant="outlined"
                label="Verified doctors · Secure payments · Private consultations"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  bgcolor: "rgba(255,255,255,0.6)",
                }}
              />
              <Typography
                variant="h2"
                sx={{ lineHeight: 1.08, mb: 2, fontSize: { xs: 36, md: 56 } }}
              >
                Healthcare that comes{" "}
                <Box
                  component="span"
                  sx={{
                    color: "primary.dark",
                    position: "relative",
                    whiteSpace: "nowrap",
                  }}
                >
                  to you
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "auto 0 -6px 0",
                      height: 14,
                      bgcolor: alpha(theme.palette.warning.main, 0.28),
                      borderRadius: 2,
                      zIndex: -1,
                    }}
                  />
                </Box>
                .
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: 17, md: 19 },
                  maxWidth: 560,
                  mb: 4,
                }}
              >
                Book verified doctors across India, consult over secure video,
                share reports, and pay — all in one link. ChikitsaLink
                connects patients and doctors, wherever you are.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mb: 4 }}
              >
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/doctors")}
                  startIcon={<SearchIcon />}
                >
                  Find a doctor
                </Button>
                {!isAuthenticated && (
                  <Button
                    size="large"
                    variant="outlined"
                    color="primary"
                    onClick={() => register("/patient/dashboard")}
                  >
                    Create free account
                  </Button>
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <TrustStat value="1,200+" label="Verified doctors" />
                <TrustStat value="25+" label="Specializations" />
                <TrustStat value="4.8 / 5" label="Average rating" />
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <HeroCard />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ───────── Specializations ───────── */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <SectionHeading
          eyebrow="Browse by specialization"
          title="Care for every need"
          subtitle="From routine check-ups to specialist advice — find the right doctor fast."
        />
        <Grid container spacing={2}>
          {specializations.map((s) => (
            <Grid item xs={6} sm={4} md={3} key={s.name}>
              <Card
                component={RouterLink}
                to={`/doctors?specialization=${encodeURIComponent(s.name)}`}
                sx={{
                  textDecoration: "none",
                  height: "100%",
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 1.25,
                    py: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.dark",
                      fontSize: 28,
                    }}
                    aria-hidden
                  >
                    {s.icon}
                  </Box>
                  <Typography fontWeight={700}>{s.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.count}+ doctors
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ───────── How it works ───────── */}
      <Container maxWidth="lg" sx={{ mt: 10 }} id="how-it-works">
        <SectionHeading
          eyebrow="How it works"
          title="Three steps to better care"
        />
        <Grid container spacing={3}>
          {howItWorks.map((step, i) => (
            <Grid item xs={12} md={4} key={step.title}>
              <Paper
                variant="outlined"
                sx={{ p: 4, height: "100%", position: "relative" }}
              >
                <Typography
                  sx={{
                    position: "absolute",
                    top: 14,
                    right: 18,
                    fontWeight: 800,
                    fontSize: 44,
                    color: alpha(theme.palette.primary.main, 0.12),
                  }}
                >
                  0{i + 1}
                </Typography>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: "primary.dark",
                    mb: 2,
                  }}
                >
                  {step.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {step.title}
                </Typography>
                <Typography color="text.secondary">{step.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ───────── Why ChikitsaLink ───────── */}
      <Container maxWidth="lg" sx={{ mt: 10 }}>
        <SectionHeading
          eyebrow="Why ChikitsaLink"
          title="Built for trust and simplicity"
        />
        <Grid container spacing={3}>
          {features.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.title}>
              <Card sx={{ p: 2, height: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(theme.palette.secondary.main, 0.12),
                      color: "secondary.dark",
                      mb: 2,
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {f.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 15 }}>
                    {f.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ───────── For doctors banner ───────── */}
      <Container maxWidth="lg" sx={{ mt: 10 }}>
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: "#fff",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            gap: 3,
            alignItems: "center",
          }}
        >
          <Box>
            <Chip
              label="For doctors"
              sx={{
                bgcolor: "rgba(255,255,255,0.18)",
                color: "#fff",
                fontWeight: 700,
                mb: 2,
              }}
            />
            <Typography variant="h4" sx={{ mb: 1 }}>
              Grow your practice, online.
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.88)" }}>
              List your clinic, set your availability, and consult patients
              securely over video. We handle verification, reminders, payments
              and notifications.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              component={RouterLink}
              to="/for-doctors"
              size="large"
              sx={{
                bgcolor: "#fff",
                color: "primary.dark",
                fontWeight: 700,
                "&:hover": { bgcolor: "#F1F8E9" },
              }}
            >
              Learn more
            </Button>
            <Button
              size="large"
              variant="outlined"
              onClick={() => registerAsDoctor("/doctor/onboarding")}
              sx={{
                borderColor: "rgba(255,255,255,0.8)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Join as a doctor
            </Button>
          </Stack>
        </Paper>
      </Container>

      {/* ───────── Testimonials ───────── */}
      <Container maxWidth="lg" sx={{ mt: 10, mb: 6 }}>
        <SectionHeading
          eyebrow="From our patients"
          title="Care people trust"
        />
        <Grid container spacing={3}>
          {testimonials.map((t) => (
            <Grid item xs={12} md={4} key={t.name}>
              <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                <Rating value={t.rating} readOnly size="small" sx={{ mb: 1 }} />
                <Typography sx={{ fontStyle: "italic", mb: 2 }}>
                  “{t.quote}”
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    {t.name[0]}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700} fontSize={14}>
                      {t.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.role}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

/* ────────────── helpers ────────────── */

function SectionHeading({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ mb: 4, textAlign: { xs: "left", md: "center" } }}>
      <Typography
        variant="overline"
        sx={{
          color: "primary.dark",
          fontWeight: 700,
          letterSpacing: 2,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography variant="h3" sx={{ mt: 0.5, fontSize: { xs: 28, md: 36 } }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          color="text.secondary"
          sx={{ mt: 1, maxWidth: 640, mx: { md: "auto" } }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function TrustStat({ value, label }: { value: string; label: string }) {
  return (
    <Stack>
      <Typography fontWeight={800} fontSize={22} color="primary.dark">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

function HeroCard() {
  const theme = useTheme();
  return (
    <Box sx={{ position: "relative" }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 5,
          border: `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #F4FBF5 100%)",
          boxShadow:
            "0 10px 40px -20px rgba(27, 94, 32, 0.35), 0 2px 6px rgba(27, 94, 32, 0.08)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 52,
              height: 52,
              fontWeight: 800,
            }}
          >
            Dr
          </Avatar>
          <Box>
            <Typography fontWeight={700}>Dr. Ananya Menon, MBBS, MD</Typography>
            <Typography variant="caption" color="text.secondary">
              General Physician · 12 yrs · Bengaluru
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip
            label="Online"
            color="success"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Box
          sx={{
            borderRadius: 3,
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            mb: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <AccessTimeIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={600}>
              Next available · Today 4:30 PM
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {["4:30", "4:45", "5:00", "5:15", "5:30"].map((t, i) => (
              <Chip
                key={t}
                label={t + " PM"}
                size="small"
                color={i === 1 ? "primary" : "default"}
                variant={i === 1 ? "filled" : "outlined"}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip icon={<VideocamIcon />} label="Video consult" />
          <Chip icon={<ChatBubbleIcon />} label="In-app chat" />
          <Chip icon={<PaymentsIcon />} label="₹500" color="warning" />
        </Stack>

        <Button fullWidth variant="contained" size="large">
          Book 4:45 PM slot
        </Button>
      </Paper>

      {/* floating mini card */}
      <Paper
        elevation={0}
        sx={{
          position: "absolute",
          left: -28,
          bottom: -28,
          p: 2,
          borderRadius: 3,
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 1.5,
          bgcolor: "#fff",
          border: `1px solid ${alpha(theme.palette.primary.dark, 0.1)}`,
          boxShadow: "0 10px 30px -20px rgba(27,94,32,0.35)",
        }}
      >
        <Avatar
          sx={{ bgcolor: alpha(theme.palette.warning.main, 0.15), color: "warning.main" }}
        >
          <LockIcon />
        </Avatar>
        <Box>
          <Typography fontSize={13} fontWeight={700}>
            Secure by design
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Secure sign-in · Encrypted traffic · Access-controlled APIs
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

function DecorBlobs() {
  const theme = useTheme();
  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -140,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: `radial-gradient(closest-side, ${alpha(
            theme.palette.primary.main,
            0.25,
          )}, transparent)`,
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: -180,
          left: -120,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(closest-side, ${alpha(
            theme.palette.secondary.main,
            0.18,
          )}, transparent)`,
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />
    </>
  );
}

/* ────────────── data ────────────── */

const specializations = [
  { name: "General Physician", count: 220, icon: "🩺" },
  { name: "Dermatology", count: 95, icon: "🌿" },
  { name: "Cardiology", count: 64, icon: "❤️" },
  { name: "Pediatrics", count: 120, icon: "👶" },
  { name: "Gynecology", count: 88, icon: "🌸" },
  { name: "Psychiatry", count: 72, icon: "🧠" },
  { name: "Orthopedics", count: 55, icon: "🦴" },
  { name: "Dentistry", count: 110, icon: "🦷" },
];

const howItWorks = [
  {
    icon: <SearchIcon />,
    title: "Find your doctor",
    desc: "Browse verified specialists, check ratings and reviews, pick the right match.",
  },
  {
    icon: <EventAvailableIcon />,
    title: "Book a slot",
    desc: "Choose a 15-minute slot that works for you and pay securely in INR.",
  },
  {
    icon: <VideocamIcon />,
    title: "Consult over video",
    desc: "Join the call from any device. Share reports and chat with your doctor.",
  },
];

const features = [
  {
    icon: <VerifiedIcon />,
    title: "Verified doctors only",
    desc: "Every doctor is verified for registration, license and credentials before going live.",
  },
  {
    icon: <LockIcon />,
    title: "Secure by default",
    desc: "Modern secure sign-in, HTTPS everywhere, strictly validated API access, and isolated per-service databases.",
  },
  {
    icon: <PaymentsIcon />,
    title: "Secure INR payments",
    desc: "Pay in INR with UPI, cards, netbanking or wallets. Automatic receipts and refunds.",
  },
  {
    icon: <ChatBubbleIcon />,
    title: "In-consult chat",
    desc: "Private chat per appointment — scoped only to the doctor and patient involved.",
  },
  {
    icon: <VideocamIcon />,
    title: "Low-latency video",
    desc: "Low-latency in-browser video with a clear waiting room so you know when the doctor joins.",
  },
  {
    icon: <HealthAndSafetyIcon />,
    title: "Report sharing",
    desc: "Upload labs and prescriptions safely. Doctors see reports only after confirmation.",
  },
];

const testimonials = [
  {
    name: "Rhea S.",
    role: "Patient · Mumbai",
    rating: 5,
    quote:
      "Booked a dermatology consult on Saturday evening — got a prescription within an hour. Saved me a clinic visit.",
  },
  {
    name: "Dr. Pradeep K.",
    role: "Cardiologist · Hyderabad",
    rating: 5,
    quote:
      "The availability scheduler and payment flow just work. I can focus on patients, not software.",
  },
  {
    name: "Aakash M.",
    role: "Patient · Pune",
    rating: 4,
    quote:
      "Loved the waiting-room screen. No confusion about when the doctor is in.",
  },
];
